import { useEffect, useState, useCallback } from 'react';
import * as Store from '@apply/store';
import { Question, QuestionType, PageQuestionProps, ShowIf, confirmModal } from '@apply/ui';
import { connect } from 'react-redux';
import { LoanType } from '@apply/constants';

interface StructureAndUnitsPageQuestionProps extends PageQuestionProps {
    loanType: LoanType;
    onChangeStructureType: (
        guid: string,
        value: Store.PropertiesStore.Types.NonRentalStructureType | Store.PropertiesStore.Types.RentalStructureType,
        loanType: LoanType
    ) => void;
    onChangeConstructionUnits: (guid: string, value: number) => void;
    onChangeRestrictedUnits: (guid: string, value: number) => void;
}

const StructureAndUnits: React.FC<StructureAndUnitsPageQuestionProps> = ({
    onQuestionValidate,
    submitted,
    property,
    loanType,
    onChangeStructureType,
    onChangeConstructionUnits,
    onChangeRestrictedUnits,
    required,
}) => {
    const [numberIncomeRestrictedUnitsValues, set_numberIncomeRestrictedUnitsValues] =
        useState<{ display: string; value: string }[]>();

    // generate options 5..35
    const numberUnitsValues = [
        { display: '', value: '' },
        ...[...Array.from(Array(31).keys())]
            .map((x) => x + 5)
            .map((count) => {
                return {
                    display: count.toString(),
                    value: count.toString(),
                };
            }),
    ];

    useEffect(() => {
        // generate options 1..units
        const numberList = [
            { display: '', value: '' },
            { display: '0', value: '0' },
            ...[...Array.from({ length: property.nonRental?.numberUnits }, (_, idx) => `${++idx}`)].map((count) => {
                return {
                    display: count.toString(),
                    value: count.toString(),
                };
            }),
        ];

        set_numberIncomeRestrictedUnitsValues(numberList);
    }, [property.nonRental?.numberUnits]);

    const rentalStructureTypeValues = [
        { display: '', value: '' },
        ...Store.PropertiesStore.Types.nonRentalStructureTypes.map((st) => {
            return {
                display: st.toString(),
                value: st.toString(),
            };
        }),
    ];

    const handleNonRentalStructureType = useCallback(
        (newvalue) =>
            onChangeStructureType(property.guid, newvalue as Store.PropertiesStore.Types.NonRentalStructureType, loanType),
        [property?.guid, loanType, onChangeStructureType]
    );

    const handleNumberConstructionUnits = (newvalue: number) => {
        const delta = newvalue - (property?.rentalUnits?.length || 0);
        const onConfirm = () => onChangeConstructionUnits(property.guid, newvalue);
        handleRentalUnitQuantityChange(delta, onConfirm);
        onChangeRestrictedUnits(property.guid, null);
    };

    const handleRentalNumberUnitsIncomeRestricted = (newvalue: number) => {
        if (newvalue == 0) {
            onChangeRestrictedUnits(property.guid, 0);
        } else {
            const result = Number(newvalue) || undefined;
            onChangeRestrictedUnits(property.guid, result);
        }
    };

    const handleRentalUnitQuantityChange = (quantityDelta: number, onConfirm: () => void) => {
        if (quantityDelta >= 0) {
            // no change or adding units, no confirmation required
            onConfirm();
        } else {
            // negative arg to slice will get the elements starting from end
            const unitsToBeRemoved = property.rentalUnits.slice(quantityDelta);
            if (unitsToBeRemoved.every((unit) => Store.PropertiesStore.PropertiesReducerHelpers.isRentalUnitEmpty(unit))) {
                // all the units to be removed are empty, no confirmation required
                onConfirm();
            } else {
                const description =
                    quantityDelta === -1
                        ? `Unit ${property.rentalUnits.length}`
                        : `Units ${property.rentalUnits.length + quantityDelta + 1}-${property.rentalUnits.length}`;
                confirmModal({
                    title: 'Confirm change',
                    message: `This selection will delete ${description}, are you sure?`,
                    onConfirm,
                });
            }
        }
    };

    return (
        <>
            <div className='form-group'>
                <Question
                    type={QuestionType.RowSelector}
                    submitted={submitted}
                    selector='structureType'
                    styleName='ungrouped'
                    onQuestionValidate={onQuestionValidate}
                    values={rentalStructureTypeValues}
                    value={property.structureType}
                    onChange={handleNonRentalStructureType}
                    required={required}
                >
                    Structure Type
                </Question>
            </div>
            <ShowIf showIf={property.structureType === '5+ Unit'}>
                <div className='form-group'>
                    <Question
                        type={QuestionType.RowSelect}
                        submitted={submitted}
                        selector='numberRentalUnits'
                        styleName='ungrouped'
                        onQuestionValidate={onQuestionValidate}
                        values={numberUnitsValues}
                        value={property.nonRental?.numberUnits?.toString() || ''}
                        onChange={handleNumberConstructionUnits}
                        required={required}
                    >
                        Units
                    </Question>
                </div>
                <ShowIf showIf={property.nonRental?.numberUnits > 0}>
                    <div className='form-group'>
                        <Question
                            type={QuestionType.RowSelect}
                            submitted={submitted}
                            selector='unitsIncomeRestricted'
                            styleName='ungrouped'
                            onQuestionValidate={onQuestionValidate}
                            values={numberIncomeRestrictedUnitsValues}
                            value={property.nonRental?.unitsIncomeRestricted?.toString() || ''}
                            onChange={handleRentalNumberUnitsIncomeRestricted}
                        >
                            Units subject to income restricted federal, state, or local housing program
                        </Question>
                    </div>
                </ShowIf>
            </ShowIf>
        </>
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => {
    return {
        property: state.properties.items.find((itm) => itm.guid === state.properties.currentPropertyId),
        loanType: state.borrower.loanType,
    };
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: {
        onChangeStructureType: (
            guid: string,
            value: Store.PropertiesStore.Types.NonRentalStructureType | Store.PropertiesStore.Types.RentalStructureType,
            loanType: LoanType
        ) => void;
        onChangeConstructionUnits: (guid: string, value: number) => void;
        onChangeRestrictedUnits: (guid: string, value: number) => void;
        dispatch: Store.ApplyDispatchType;
    } = {
        onChangeStructureType: (
            guid: string,
            value: Store.PropertiesStore.Types.NonRentalStructureType | Store.PropertiesStore.Types.RentalStructureType,
            loanType: LoanType
        ) => dispatch(Store.PropertiesStore.Actions.updateStructureType(guid, value, loanType)),
        onChangeConstructionUnits: (guid: string, value: number) =>
            dispatch(Store.PropertiesStore.Actions.updateNumberConstructionUnits(guid, value)),
        onChangeRestrictedUnits: (guid: string, value: number) =>
            dispatch(Store.PropertiesStore.Actions.updateNumberUnitsIncomeRestricted(guid, value)),
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(StructureAndUnits);
